import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "../icons/Icon";
import { Aviso, Campo, Grupo, Item } from "../components/Layout";
import {
  api, cpfValido, fmt,
  type CandidatoCriado, type Edital, type ErroApi, type Regras,
} from "../lib/api";

/* -------------------------------------------------------------- modelo --- */

/** Anexo II: item A = Rede Baixo Guandu (0,2 ponto/mês); item B = Outras Redes (0,1). */
type Rede = "MUNICIPAL_BG" | "ESTADUAL";

const REDES: Array<{ valor: Rede; rotulo: string }> = [
  { valor: "MUNICIPAL_BG", rotulo: "Rede Baixo Guandu" },
  { valor: "ESTADUAL", rotulo: "Outras Redes" },
];

type Experiencia = {
  vinculo: Rede;
  dataInicio: string; dataFim: string;
};

type Qualificacao = {
  categoria: "I" | "II"; cargaHoraria: string; dataConclusao: string;
};

type Formulario = {
  nome: string; cpf: string; dataNascimento: string; rg: string;
  rgOrgaoExpedidor: string; email: string; telefone: string; telefoneAlternativo: string;
  logradouro: string; numero: string; complemento: string; bairro: string;
  cidade: string; uf: string; cep: string;
  experiencias: Experiencia[];
  qualificacoes: Qualificacao[];
};

const VAZIO: Formulario = {
  nome: "", cpf: "", dataNascimento: "", rg: "", rgOrgaoExpedidor: "",
  email: "", telefone: "", telefoneAlternativo: "",
  logradouro: "", numero: "", complemento: "", bairro: "",
  cidade: "Baixo Guandu", uf: "ES", cep: "",
  experiencias: [], qualificacoes: [],
};

const PASSOS = [
  "Dados pessoais",
  "Experiência profissional",
  "Formação",
  "Confira antes de enviar",
] as const;

/* ---------------------------------------------------------- componente --- */

/**
 * Cadastro do perfil do candidato: dados pessoais, experiência e formação,
 * item 8.5. É um perfil único — depois de criar a senha, o candidato usa
 * esses mesmos dados pra se inscrever em quantos cargos quiser (item 8.9),
 * sem repetir nada, de dentro da área logada.
 */
export function Inscricao({ edital }: { edital: Edital }) {
  const [passo, setPasso] = useState(0);
  const [form, setForm] = useState<Formulario>(VAZIO);
  const [erros, setErros] = useState<string[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [criado, setCriado] = useState<CandidatoCriado | null>(null);
  const navigate = useNavigate();

  // A pontuação ao vivo precisa de uma tabela de pontos pra calcular; como o
  // cargo só é escolhido depois (dentro da conta), usa a do primeiro cargo
  // do edital — os pontos de experiência são os mesmos em todos os grupos.
  const cargoParaCalculo = String(edital.cargos[0]?.id ?? "");
  const [regrasSimulacao, setRegrasSimulacao] = useState<Regras | null>(null);

  const resumoErroRef = useRef<HTMLDivElement>(null);
  const tituloRef = useRef<HTMLHeadingElement>(null);

  const fase = edital.cronograma.find((f) => f.codigo === "INSCRICAO");
  const aberta = fase?.aberta ?? false;

  useEffect(() => { tituloRef.current?.focus(); }, [passo]);
  useEffect(() => {
    if (erros.length) resumoErroRef.current?.focus();
  }, [erros]);

  useEffect(() => {
    if (!cargoParaCalculo) { setRegrasSimulacao(null); return; }
    let ativo = true;
    api.get<Regras>(`/api/publico/cargos/${cargoParaCalculo}/regras`)
      .then((r) => { if (ativo) setRegrasSimulacao(r); })
      .catch(() => { if (ativo) setRegrasSimulacao(null); });
    return () => { ativo = false; };
  }, [cargoParaCalculo]);

  const calculo = useCalculo(form, regrasSimulacao);

  function atualizar(mudanca: Partial<Formulario>) {
    setForm((f) => ({ ...f, ...mudanca }));
  }

  function avancar() {
    const problemas = validarPasso(passo, form, edital);
    setErros(problemas);
    if (problemas.length === 0) {
      setPasso((p) => Math.min(p + 1, PASSOS.length - 1));
      window.scrollTo({ top: 0 });
    }
  }

  function voltar() {
    setErros([]);
    setPasso((p) => Math.max(p - 1, 0));
    window.scrollTo({ top: 0 });
  }

  async function enviar() {
    const problemas = validarPasso(PASSOS.length - 1, form, edital);
    if (problemas.length) { setErros(problemas); return; }

    setEnviando(true);
    setErros([]);
    try {
      const corpo = {
        ...form,
        cpf: form.cpf.replace(/\D/g, ""),
        cep: form.cep.replace(/\D/g, ""),
        telefone: form.telefone.replace(/\D/g, ""),
        telefoneAlternativo: form.telefoneAlternativo.replace(/\D/g, "") || null,
        experiencias: form.experiencias.map((e) => ({ ...e, dataFim: e.dataFim || null })),
        qualificacoes: form.qualificacoes.map((q) => ({
          ...q,
          cargaHoraria: q.cargaHoraria ? Number(q.cargaHoraria) : null,
          dataConclusao: q.dataConclusao || null,
        })),
      };

      const r = await api.post<CandidatoCriado>("/api/publico/candidatos", corpo);
      setCriado(r);
      window.scrollTo({ top: 0 });
    } catch (e) {
      const err = e as ErroApi;
      setErros(err.motivos.length ? err.motivos : [err.mensagem]);
    } finally {
      setEnviando(false);
    }
  }

  if (criado?.temSenha) {
    return (
      <section className="secao">
        <div className="container container-estreito pilha pilha-6">
          <Aviso tipo="info" titulo="Este CPF já tem conta">
            Seus dados foram atualizados. Como este CPF já tem senha cadastrada, entre na sua
            conta pra continuar: é lá que você escolhe o cargo e envia a inscrição.
          </Aviso>
          <button type="button" className="btn btn-primario btn-largo"
                  onClick={() => navigate("/entrar")}>
            Entrar na minha conta
          </button>
        </div>
      </section>
    );
  }

  if (criado) {
    return (
      <section className="secao">
        <div className="container container-estreito pilha pilha-6">
          <div className="cartao destaque-senha pilha pilha-4">
            <Icon nome="checado" tamanho={28} />
            <h1 className="h2">Cadastro salvo!</h1>
            <p className="texto-secundario">
              Agora crie uma senha pra acessar sua conta. É nela que você escolhe o cargo,
              envia a inscrição e acompanha tudo, inclusive as suas próximas inscrições
              (até 5, item 8.9), sem precisar preencher os dados de novo.
            </p>
            <button type="button" className="btn btn-primario btn-largo"
                    onClick={() => navigate("/criar-senha", {
                      state: { cpf: criado.cpf, dataNascimento: form.dataNascimento },
                    })}>
              <Icon nome="cadeado" tamanho={18} />
              Criar minha senha
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (!aberta) {
    return (
      <section className="secao">
        <div className="container container-estreito pilha">
          <h1>Inscrições</h1>
          <Aviso tipo="atencao" titulo="Fora do período de inscrição">
            {fase
              ? <>O período de inscrição é de <strong>{fase.periodoFormatado}</strong>. Fora
                 desse prazo, o sistema não aceita inscrição por nenhuma outra via.</>
              : "O período de inscrição não está configurado neste edital."}
          </Aviso>
        </div>
      </section>
    );
  }

  return (
    <section className="secao">
      <div className="container">
        <div className="grade grade-lateral grade-lateral-300">
          <div className="pilha pilha-6">
            {/* ------------------------------------------- cabeçalho etapa */}
            <div className="pilha pilha-2">
              {passo > 0 && (
                <button type="button" className="voltar" onClick={voltar}>
                  <Icon nome="esquerda" tamanho={16} />
                  Voltar
                </button>
              )}
              <p className="wizard-passo">Etapa {passo + 1} de {PASSOS.length}</p>
              <ol className="wizard-etapas" aria-hidden="true">
                {PASSOS.map((nome, i) => (
                  <li key={nome} className={i < passo ? "feita" : i === passo ? "atual" : ""} />
                ))}
              </ol>
              <h1 ref={tituloRef} tabIndex={-1}>{PASSOS[passo]}</h1>
            </div>

            {/* ------------------------------------------ resumo de erros */}
            {erros.length > 0 && (
              <div className="resumo-erros" ref={resumoErroRef} tabIndex={-1} role="alert">
                <h2>Há {erros.length === 1 ? "um problema" : `${erros.length} problemas`} a corrigir</h2>
                <ul>
                  {erros.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}

            {/* ------------------------------------------------- conteúdo */}
            {passo === 0 && <PassoPessoais form={form} atualizar={atualizar} />}
            {passo === 1 && <PassoExperiencia form={form} atualizar={atualizar} />}
            {passo === 2 && <PassoFormacao form={form} atualizar={atualizar} />}
            {passo === 3 && <PassoRevisao form={form} irPara={setPasso} />}

            <div className="wizard-acoes">
              {passo > 0 ? (
                <button type="button" className="btn btn-secundario" onClick={voltar}>
                  <Icon nome="esquerda" tamanho={18} />
                  Voltar
                </button>
              ) : <span />}

              {passo < PASSOS.length - 1 ? (
                <button type="button" className="btn btn-primario" onClick={avancar}>
                  Continuar
                </button>
              ) : (
                <button type="button" className="btn btn-primario" onClick={enviar} disabled={enviando}>
                  {enviando ? <span className="carregando" /> : <Icon nome="checado" tamanho={18} />}
                  {enviando ? "Enviando" : "Salvar cadastro"}
                </button>
              )}
            </div>
          </div>

          <Recibo regras={regrasSimulacao} calculo={calculo} />
        </div>
      </div>
    </section>
  );
}

function Recibo({ regras, calculo }: {
  regras: Regras | null; calculo: ReturnType<typeof useCalculo>;
}) {
  return (
    <aside className="calculo recibo" aria-label="Pontuação de experiência declarada até agora">
      <div className="recibo-topo">
        <p className="recibo-titulo">Pontos de experiência</p>
        <p className="recibo-sub">Atualiza enquanto você preenche</p>
      </div>

      <dl className="recibo-linhas">
        <div>
          <dt>
            Experiência
            <small>{calculo.mesesConsiderados} de {regras?.tetoMeses ?? 30} meses considerados</small>
          </dt>
          <dd className="num">{fmt.pontos(calculo.experiencia)}</dd>
        </div>
      </dl>

      <div className="recibo-corte" aria-hidden="true" />

      <div className="recibo-total">
        <span>Total</span>
        <span className="num">{fmt.pontos(calculo.experiencia)}</span>
      </div>

      <p className="recibo-nota">
        Esta pontuação não varia conforme o cargo. A pontuação de formação será
        calculada após a escolha do cargo, na sua conta.
      </p>

      {calculo.avisos.map((a, i) => (
        <p key={i} className="recibo-nota recibo-aviso">{a}</p>
      ))}
    </aside>
  );
}

/* --------------------------------------------------------------- etapas --- */

function PassoPessoais({ form, atualizar }: {
  form: Formulario; atualizar: (m: Partial<Formulario>) => void;
}) {
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [erroCep, setErroCep] = useState<string | null>(null);

  async function aoMudarCep(v: string) {
    const formatado = fmt.cep(v);
    atualizar({ cep: formatado });
    setErroCep(null);

    const digitos = v.replace(/\D/g, "");
    if (digitos.length !== 8) return;

    setBuscandoCep(true);
    try {
      const r = await fetch(`https://viacep.com.br/ws/${digitos}/json/`);
      const dados = await r.json();
      if (dados.erro) {
        setErroCep("CEP não encontrado. Preencha o endereço manualmente.");
        return;
      }
      atualizar({
        logradouro: dados.logradouro || "",
        bairro: dados.bairro || "",
        cidade: dados.localidade || "",
        uf: dados.uf || "",
      });
    } catch {
      setErroCep("Não foi possível buscar o CEP agora. Preencha o endereço manualmente.");
    } finally {
      setBuscandoCep(false);
    }
  }

  return (
    <div className="cartao pilha">
      <p className="texto-secundario">
        Informe nome completo, data de nascimento, CPF, RG e endereço residencial completo.
        Divergência entre a data de nascimento informada e a constante no CPF e no RG torna o
        candidato inapto à contratação.
      </p>

      <Campo rotulo="Nome completo" id="nome" valor={form.nome}
             aoMudar={(v) => atualizar({ nome: v })} autoComplete="name" />

      <div className="grade grade-2">
        <Campo rotulo="CPF" id="cpf" valor={form.cpf} inputMode="numeric"
               ajuda="Somente números. Também é o seu login"
               aoMudar={(v) => atualizar({ cpf: fmt.cpf(v) })} />
        <Campo rotulo="Data de nascimento" id="nascimento" tipo="date"
               valor={form.dataNascimento} aoMudar={(v) => atualizar({ dataNascimento: v })} />
      </div>

      <div className="grade grade-2">
        <Campo rotulo="RG" id="rg" valor={form.rg} aoMudar={(v) => atualizar({ rg: v })} />
        <Campo rotulo="Órgão expedidor" id="rgOrgao" valor={form.rgOrgaoExpedidor}
               ajuda="Exemplo: SSP/ES" aoMudar={(v) => atualizar({ rgOrgaoExpedidor: v })} />
      </div>

      <div className="grade grade-2">
        <Campo rotulo="E-mail" id="email" tipo="email" valor={form.email}
               ajuda="Usado para avisos sobre as suas inscrições"
               autoComplete="email" aoMudar={(v) => atualizar({ email: v })} />
        <Campo rotulo="Telefone" id="telefone" valor={form.telefone} inputMode="tel"
               ajuda="Com DDD" autoComplete="tel"
               aoMudar={(v) => atualizar({ telefone: fmt.telefone(v) })} />
      </div>

      <hr className="regua" />

      <Campo rotulo="CEP" id="cep" valor={form.cep} inputMode="numeric"
             ajuda={buscandoCep
               ? "Buscando endereço..."
               : (erroCep ?? "Digite o CEP para preencher o endereço automaticamente")}
             aoMudar={aoMudarCep} />

      <div className="grade grade-2-1">
        <Campo rotulo="Logradouro" id="logradouro" valor={form.logradouro}
               autoComplete="address-line1" aoMudar={(v) => atualizar({ logradouro: v })} />
        <Campo rotulo="Número" id="numero" valor={form.numero}
               aoMudar={(v) => atualizar({ numero: v })} />
      </div>

      <div className="grade grade-2">
        <Campo rotulo="Complemento" id="complemento" valor={form.complemento}
               ajuda="Opcional" aoMudar={(v) => atualizar({ complemento: v })} />
        <Campo rotulo="Bairro" id="bairro" valor={form.bairro}
               aoMudar={(v) => atualizar({ bairro: v })} />
      </div>

      <div className="grade grade-2">
        <Campo rotulo="Cidade" id="cidade" valor={form.cidade}
               aoMudar={(v) => atualizar({ cidade: v })} />
        <Campo rotulo="Estado" id="uf" valor={form.uf} ajuda="Sigla, 2 letras"
               aoMudar={(v) => atualizar({ uf: v.toUpperCase().slice(0, 2) })} />
      </div>
    </div>
  );
}

function PassoExperiencia({ form, atualizar }: {
  form: Formulario; atualizar: (m: Partial<Formulario>) => void;
}) {
  function adicionar() {
    atualizar({
      experiencias: [...form.experiencias, {
        vinculo: "MUNICIPAL_BG",
        dataInicio: "", dataFim: "",
      }],
    });
  }

  function alterar(i: number, mudanca: Partial<Experiencia>) {
    const lista = [...form.experiencias];
    lista[i] = { ...lista[i], ...mudanca };
    atualizar({ experiencias: lista });
  }

  return (
    <div className="pilha">
      <div className="cartao pilha pilha-2">
        <p className="texto-secundario">
          Informe os períodos trabalhados em educação e a rede de ensino de cada um. Esse
          histórico fica salvo no seu perfil: a pontuação é calculada depois, para cada cargo
          em que você se inscrever.
        </p>
      </div>

      {form.experiencias.map((exp, i) => (
        <div key={i} className="item-removivel pilha">
          <div className="linha linha-entre">
            <strong>Período {i + 1}</strong>
            <button type="button" className="btn btn-sutil btn-pequeno"
                    onClick={() => atualizar({ experiencias: form.experiencias.filter((_, j) => j !== i) })}>
              <Icon nome="cancelar" tamanho={16} />
              Remover
            </button>
          </div>

          <div className="campo">
            <label className="campo-rotulo" htmlFor={`exp-vinc-${i}`}>Rede de ensino</label>
            <select id={`exp-vinc-${i}`} className="selecao" value={exp.vinculo}
                    onChange={(e) => alterar(i, { vinculo: e.target.value as Rede })}>
              {REDES.map((r) => <option key={r.valor} value={r.valor}>{r.rotulo}</option>)}
            </select>
          </div>

          <div className="grade grade-2">
            <Campo rotulo="Início" id={`exp-ini-${i}`} tipo="date" valor={exp.dataInicio}
                   aoMudar={(v) => alterar(i, { dataInicio: v })} />
            <Campo rotulo="Fim" id={`exp-fim-${i}`} tipo="date" valor={exp.dataFim}
                   ajuda="Deixe em branco se ainda trabalha lá"
                   aoMudar={(v) => alterar(i, { dataFim: v })} />
          </div>
        </div>
      ))}

      <button type="button" className="btn btn-secundario" onClick={adicionar}>
        <Icon nome="editar" tamanho={18} />
        Adicionar período de experiência
      </button>

      {form.experiencias.length === 0 && (
        <p className="texto-sutil">
          Se você não tem experiência a declarar, siga para a próxima etapa.
        </p>
      )}
    </div>
  );
}

function PassoFormacao({ form, atualizar }: {
  form: Formulario; atualizar: (m: Partial<Formulario>) => void;
}) {
  function adicionar(categoria: "I" | "II") {
    atualizar({
      qualificacoes: [...form.qualificacoes, { categoria, cargaHoraria: "", dataConclusao: "" }],
    });
  }

  function alterar(i: number, mudanca: Partial<Qualificacao>) {
    const lista = [...form.qualificacoes];
    lista[i] = { ...lista[i], ...mudanca };
    atualizar({ qualificacoes: lista });
  }

  function remover(i: number) {
    atualizar({ qualificacoes: form.qualificacoes.filter((_, j) => j !== i) });
  }

  return (
    <div className="pilha">
      <Aviso tipo="info">
        Declare aqui os títulos que você possui: formação acadêmica (graduação,
        pós-graduação) e cursos de formação continuada. A pontuação de cada um depende do
        cargo, e você escolhe qual título usar quando for se inscrever.
      </Aviso>

      {form.qualificacoes.map((q, i) => (
        <div key={i} className="item-removivel pilha">
          <div className="linha linha-entre">
            <strong>
              {q.categoria === "I" ? "Formação acadêmica / titulação" : "Curso de formação continuada"}
            </strong>
            <button type="button" className="btn btn-sutil btn-pequeno" onClick={() => remover(i)}>
              <Icon nome="cancelar" tamanho={16} />
              Remover
            </button>
          </div>

          <div className="grade grade-2">
            <Campo rotulo="Carga horária" id={`qual-ch-${i}`} inputMode="numeric"
                   ajuda="Em horas" valor={q.cargaHoraria}
                   aoMudar={(v) => alterar(i, { cargaHoraria: v.replace(/\D/g, "") })} />
            <Campo rotulo="Data de conclusão" id={`qual-data-${i}`} tipo="date"
                   ajuda={q.categoria === "I" ? "Opcional" : undefined} valor={q.dataConclusao}
                   aoMudar={(v) => alterar(i, { dataConclusao: v })} />
          </div>
        </div>
      ))}

      <div className="linha linha-quebra">
        <button type="button" className="btn btn-secundario" onClick={() => adicionar("I")}>
          <Icon nome="editar" tamanho={18} />
          Adicionar formação acadêmica
        </button>
        <button type="button" className="btn btn-secundario" onClick={() => adicionar("II")}>
          <Icon nome="editar" tamanho={18} />
          Adicionar curso de formação
        </button>
      </div>

      {form.qualificacoes.length === 0 && (
        <p className="texto-sutil">
          Se você não tem título a declarar, siga para a próxima etapa.
        </p>
      )}
    </div>
  );
}

function PassoRevisao({ form, irPara }: {
  form: Formulario;
  irPara: (p: number) => void;
}) {
  return (
    <div className="pilha">
      <p className="texto-secundario medida">
        Confira os dados antes de salvar. As informações prestadas são de sua inteira
        responsabilidade, e declarações falsas ou inexatas cancelam a inscrição e anulam todos
        os atos dela decorrentes.
      </p>

      <div className="painel">
        <div className="painel-corpo">
          <Grupo titulo="Dados pessoais" aoEditar={() => irPara(0)}>
            <Item rotulo="Nome">{form.nome}</Item>
            <Item rotulo="CPF">{form.cpf}</Item>
            <Item rotulo="Data de nascimento">{fmt.data(form.dataNascimento)}</Item>
            <Item rotulo="RG">{form.rg} {form.rgOrgaoExpedidor}</Item>
            <Item rotulo="E-mail">{form.email}</Item>
            <Item rotulo="Telefone">{form.telefone}</Item>
            <Item rotulo="Endereço">
              {form.logradouro}, {form.numero}
              {form.complemento ? `, ${form.complemento}` : ""}, {form.bairro},{" "}
              {form.cidade}/{form.uf}, CEP {form.cep}
            </Item>
          </Grupo>

          <Grupo titulo="Experiência profissional" aoEditar={() => irPara(1)}>
            {form.experiencias.length === 0 && <Item rotulo="Períodos">Nenhum declarado</Item>}
            {form.experiencias.map((e, i) => (
              <Item key={i} rotulo={`Período ${i + 1}`}>
                <span className="texto-sutil">
                  {REDES.find((r) => r.valor === e.vinculo)?.rotulo}
                </span>
                <br />
                <span className="texto-sutil num">
                  {fmt.data(e.dataInicio)} a {e.dataFim ? fmt.data(e.dataFim) : "hoje (em andamento)"}
                </span>
              </Item>
            ))}
          </Grupo>

          <Grupo titulo="Formação" aoEditar={() => irPara(2)}>
            {form.qualificacoes.length === 0 && <Item rotulo="Títulos">Nenhum declarado</Item>}
            {form.qualificacoes.map((q, i) => (
              <Item key={i} rotulo={q.categoria === "I" ? "Formação acadêmica" : "Curso de formação"}>
                {q.cargaHoraria ? `${q.cargaHoraria}h` : "Carga horária não informada"}
                {q.dataConclusao ? ` · concluído em ${fmt.data(q.dataConclusao)}` : ""}
              </Item>
            ))}
          </Grupo>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ validação --- */

function validarPasso(passo: number, form: Formulario, edital: Edital): string[] {
  const erros: string[] = [];

  if (passo === 0) {
    if (!form.nome.trim() || !form.nome.includes(" ")) {
      erros.push("Informe o seu nome completo, com sobrenome.");
    }
    if (!cpfValido(form.cpf)) erros.push("Informe um CPF válido, com 11 dígitos.");
    if (!form.dataNascimento) {
      erros.push("Informe a sua data de nascimento.");
    } else {
      const nasc = new Date(form.dataNascimento);
      const limite = new Date();
      limite.setFullYear(limite.getFullYear() - edital.idadeMinima);
      if (nasc > limite) {
        erros.push(`É exigida idade mínima de ${edital.idadeMinima} anos completos na data da convocação.`);
      }
    }
    if (!form.rg.trim()) erros.push("Informe o número do seu RG.");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) {
      erros.push("Informe um e-mail válido.");
    }
    if (form.telefone.replace(/\D/g, "").length < 10) erros.push("Informe o telefone com DDD.");
    if (!form.logradouro.trim()) erros.push("Informe o logradouro do seu endereço.");
    if (!form.numero.trim()) erros.push("Informe o número do endereço.");
    if (!form.bairro.trim()) erros.push("Informe o bairro.");
    if (!form.cidade.trim()) erros.push("Informe a cidade.");
    if (form.uf.length !== 2) erros.push("Informe a sigla do estado, com 2 letras.");
    if (form.cep.replace(/\D/g, "").length !== 8) erros.push("Informe o CEP com 8 dígitos.");
  }

  if (passo === 1) {
    form.experiencias.forEach((e, i) => {
      const n = i + 1;
      if (!e.dataInicio) erros.push(`Período ${n}: informe a data de início.`);
      else if (new Date(e.dataInicio) > new Date()) {
        erros.push(`Período ${n}: a data de início não pode estar no futuro.`);
      }
      // Data de fim é opcional: em branco significa que o candidato ainda
      // trabalha lá (o edital não exige que o vínculo já tenha terminado).
      if (e.dataFim && new Date(e.dataFim) < new Date(e.dataInicio)) {
        erros.push(`Período ${n}: a data de fim não pode ser anterior à de início.`);
      }
    });
  }

  if (passo === 2) {
    form.qualificacoes.forEach((q, i) => {
      const rotulo = q.categoria === "I" ? "Formação acadêmica" : "Curso de formação";
      const n = i + 1;
      if (!q.cargaHoraria) erros.push(`${rotulo} ${n}: informe a carga horária.`);
      // Categoria II (Anexo II): toda alínea de curso de formação continuada exige
      // conclusão dentro de uma janela de anos — sem data não há como classificar.
      if (q.categoria === "II" && !q.dataConclusao) {
        erros.push(`${rotulo} ${n}: informe a data de conclusão.`);
      }
    });
  }

  return erros;
}

/* -------------------------------------------------- cálculo transparente --- */

type Calculo = {
  experiencia: number; mesesConsiderados: number; avisos: string[];
};

function codigoExperiencia(e: Pick<Experiencia, "vinculo">): string {
  return e.vinculo === "MUNICIPAL_BG" ? "A" : "B";
}

/** Hoje em formato ISO (yyyy-mm-dd), pro mesmo Date.parse() usado nos períodos. */
function hojeISO(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Espelha a conta do servidor pros pontos de experiência (os mesmos em
 * qualquer cargo). Formação não entra aqui: depende da alínea do cargo
 * escolhido, que só existe ao criar a inscrição.
 */
function useCalculo(form: Formulario, regras: Regras | null): Calculo {
  return useMemo(() => {
    const avisos: string[] = [];
    if (!regras) {
      return { experiencia: 0, mesesConsiderados: 0, avisos };
    }

    // --- experiência: funde intervalos, desconta sobreposição, aplica o teto
    const porRegra = [...regras.experiencia].sort((a, b) => b.pontosPorMes - a.pontosPorMes);
    const consumido: Array<[number, number]> = [];
    let totalExp = 0;
    let mesesUsados = 0;
    let houveSobreposicao = false;

    for (const regra of porRegra) {
      const intervalos = form.experiencias
        .filter((e) => codigoExperiencia(e) === regra.codigo && e.dataInicio)
        // Sem data de fim = ainda em andamento: conta até hoje, igual o servidor faz.
        .map((e) => [Date.parse(e.dataInicio), Date.parse(e.dataFim || hojeISO()) + 86_400_000] as [number, number])
        .filter(([a, b]) => Number.isFinite(a) && Number.isFinite(b) && b > a);

      const fundidos = fundir(intervalos);
      const brutos = fundidos.reduce((s, [a, b]) => s + (b - a), 0);
      const liquidos = subtrair(fundidos, consumido);
      const dias = Math.round(liquidos.reduce((s, [a, b]) => s + (b - a), 0) / 86_400_000);
      if (liquidos.reduce((s, [a, b]) => s + (b - a), 0) < brutos) houveSobreposicao = true;

      consumido.push(...fundidos);

      const mesesApurados = Math.floor(dias / regras.diasPorMes);
      const disponivel = Math.max(0, regras.tetoMeses - mesesUsados);
      const mesesPontuados = Math.min(mesesApurados, disponivel);
      if (mesesPontuados < mesesApurados) {
        avisos.push(`Tempo limitado a ${regras.tetoMeses} meses pelo teto de pontuação.`);
      }
      mesesUsados += mesesPontuados;
      totalExp += mesesPontuados * regra.pontosPorMes;
    }
    if (houveSobreposicao) {
      avisos.push("Períodos sobrepostos foram contados uma única vez.");
    }

    const exp = Math.round(totalExp * 100) / 100;
    return {
      experiencia: exp,
      mesesConsiderados: mesesUsados,
      avisos: [...new Set(avisos)],
    };
  }, [form, regras]);
}

function fundir(intervalos: Array<[number, number]>): Array<[number, number]> {
  if (!intervalos.length) return [];
  const ordenados = [...intervalos].sort((a, b) => a[0] - b[0]);
  const saida: Array<[number, number]> = [[...ordenados[0]] as [number, number]];
  for (let i = 1; i < ordenados.length; i++) {
    const atual = saida[saida.length - 1];
    const prox = ordenados[i];
    if (prox[0] <= atual[1]) atual[1] = Math.max(atual[1], prox[1]);
    else saida.push([...prox] as [number, number]);
  }
  return saida;
}

function subtrair(base: Array<[number, number]>, remover: Array<[number, number]>): Array<[number, number]> {
  if (!remover.length) return base;
  const corte = fundir(remover);
  const saida: Array<[number, number]> = [];
  for (const [inicioBase, fimBase] of base) {
    let inicio = inicioBase;
    for (const [a, b] of corte) {
      if (b <= inicio) continue;
      if (a >= fimBase) break;
      if (a > inicio) saida.push([inicio, Math.min(a, fimBase)]);
      inicio = Math.max(inicio, b);
      if (inicio >= fimBase) break;
    }
    if (inicio < fimBase) saida.push([inicio, fimBase]);
  }
  return saida;
}
